import { startTransition, useCallback, useEffect, useState } from 'react';
import { queueHomepageTabChange, shownHomepageTab } from './homepageTabChange';

/**
 * Homepage tab tap that paints at once (INP).
 *
 * The header highlights the tapped tab from its own state, so the urgent render is only the
 * header. The page switch (form swap, URL, campaign product) runs as a transition, which React
 * can render in the background without holding the tap's next paint.
 *
 * @param {'flight' | 'hotel' | 'train'} activeFormType Tab the homepage currently shows.
 * @param {(type: string) => void} [onFormTypeChange]
 * @returns {[string, (type: string) => boolean]} Tab to highlight, and the tap handler.
 */
export function useHomepageTabPress(activeFormType, onFormTypeChange) {
  const [pressedTab, setPressedTab] = useState(null);

  // Once the page has switched (or moved to another tab some other way), follow it again.
  useEffect(() => {
    setPressedTab(null);
  }, [activeFormType]);

  const shownTab = shownHomepageTab(pressedTab, activeFormType);

  const pressTab = useCallback(
    (type) => queueHomepageTabChange(type, shownTab, typeof onFormTypeChange === 'function'
      ? (next) => {
        setPressedTab(next);
        startTransition(() => onFormTypeChange(next));
      }
      : undefined),
    [onFormTypeChange, shownTab],
  );

  return [shownTab, pressTab];
}
